/**
 * The event engine.
 *
 * Selection is weighted, condition-gated and memory-aware, so the same life
 * never sees the same card twice in a row and a card only appears when the
 * state it talks about actually exists. Resolution is two-phase: a choice may
 * be *intended* and then fail to happen (the inertia system), and only if it
 * happens does the weighted outcome roll.
 */

import { Rng } from './rng';
import type {
  DecisionRecord,
  LogEntry,
  PlayerState,
  StatDelta,
} from './types';
import type { Choice, GameEvent, Outcome } from './content/schema';
import { EVENTS_BY_ID, EVENTS_BY_POOL } from './content';
import { evaluateAll, firstUnmet } from './conditions';
import type { EffectContext } from './effects';
import { applyEffects, pushLog } from './effects';
import { inertiaChance } from './systems';
import { STAT_KEYS } from './types';

/* ------------------------------------------------------------------ *
 * Eligibility
 * ------------------------------------------------------------------ */

export function isEligible(state: PlayerState, event: GameEvent): boolean {
  if (event.ageMin !== undefined && state.age < event.ageMin) return false;
  if (event.ageMax !== undefined && state.age > event.ageMax) return false;
  if (event.stages && !event.stages.includes(state.stage)) return false;

  const count = state.eventMemory.counts[event.id] ?? 0;
  if (event.unique && count > 0) return false;
  if (event.cooldown) {
    const last = state.eventMemory.lastSeenAge[event.id];
    if (last !== undefined && state.age - last < event.cooldown) return false;
  }
  if (!evaluateAll(state, event.requires)) return false;
  if (event.excludes && event.excludes.some((c) => evaluateAll(state, [c]))) return false;

  // An event is only playable if at least one choice is takeable or shown.
  const usable = event.choices.some(
    (c) => (c.showWhenLocked ?? false) || evaluateAll(state, c.requires),
  );
  return usable;
}

export function effectiveWeight(state: PlayerState, event: GameEvent): number {
  if (event.weight <= 0) return 0;
  let w = event.weight;
  for (const mod of event.weightMods ?? []) {
    if (evaluateAll(state, mod.conditions)) w *= mod.multiplier;
  }
  // Recently seen events are strongly de-prioritised even past their cooldown.
  const last = state.eventMemory.lastSeenAge[event.id];
  if (last !== undefined) {
    const gap = state.age - last;
    if (gap < 12) w *= 0.25 + (gap / 12) * 0.75;
  }
  const count = state.eventMemory.counts[event.id] ?? 0;
  if (count > 0) w *= 1 / (1 + count * 0.45);
  return Math.max(0, w);
}

/**
 * Draw an event for the player's current node.
 *
 * Priority order:
 *   1. queued events (follow-ups, forced chains) — these are the spine of
 *      multi-step stories and must never be skipped;
 *   2. weighted draw from the node's pools;
 *   3. a fallback quiet year, so the engine can always produce something.
 */
export function drawEvent(state: PlayerState, pools: string[]): GameEvent | null {
  while (state.pendingEvents.length > 0) {
    const id = state.pendingEvents.shift() as string;
    const event = EVENTS_BY_ID[id];
    if (!event) continue;
    // Queued events bypass cooldown but still respect hard requirements.
    if (event.requires && !evaluateAll(state, event.requires)) continue;
    if (event.ageMax !== undefined && state.age > event.ageMax) continue;
    return event;
  }

  const seen = new Set<string>();
  const candidates: { event: GameEvent; weight: number }[] = [];
  for (const pool of pools) {
    for (const event of EVENTS_BY_POOL[pool] ?? []) {
      if (seen.has(event.id)) continue;
      seen.add(event.id);
      if (!isEligible(state, event)) continue;
      const weight = effectiveWeight(state, event);
      if (weight > 0) candidates.push({ event, weight });
    }
  }
  if (candidates.length === 0) return EVENTS_BY_ID['life.quietYear'] ?? null;

  const rng = Rng.fromState(state.rng);
  const idx = rng.weightedIndex(candidates.map((c) => c.weight));
  state.rng = rng.toState();
  return candidates[idx < 0 ? 0 : idx]?.event ?? null;
}

/* ------------------------------------------------------------------ *
 * Presentation model
 * ------------------------------------------------------------------ */

export interface PresentedChoice {
  choice: Choice;
  enabled: boolean;
  lockedReason: string | null;
}

export interface PresentedEvent {
  event: GameEvent;
  choices: PresentedChoice[];
}

export function presentEvent(state: PlayerState, event: GameEvent): PresentedEvent {
  const choices: PresentedChoice[] = [];
  for (const choice of event.choices) {
    const enabled = evaluateAll(state, choice.requires);
    if (!enabled && !(choice.showWhenLocked ?? false)) continue;
    choices.push({ choice, enabled, lockedReason: enabled ? null : firstUnmet(state, choice.requires) });
  }
  return { event, choices };
}

/* ------------------------------------------------------------------ *
 * Resolution
 * ------------------------------------------------------------------ */

function pickOutcome(state: PlayerState, choice: Choice, rng: Rng): Outcome | undefined {
  const eligible = choice.outcomes.filter((o) => evaluateAll(state, o.requires));
  const pool = eligible.length > 0 ? eligible : choice.outcomes;
  const weights = pool.map((o) => {
    let w = o.weight;
    for (const fav of o.favouredBy ?? []) {
      if (evaluateAll(state, fav.conditions)) w *= fav.multiplier;
    }
    return Math.max(0, w);
  });
  const idx = rng.weightedIndex(weights);
  return pool[idx < 0 ? 0 : idx];
}

export interface ResolutionResult {
  /** The narrative line to show the player. */
  narrative: string;
  outcomeLabel: string;
  /** True when inertia intercepted the choice. */
  deflected: boolean;
  entries: LogEntry[];
  deltas: StatDelta[];
}

function snapshot(state: PlayerState): Record<string, number> {
  const out: Record<string, number> = {};
  for (const k of STAT_KEYS) out[k] = state.stats[k];
  out['cash'] = state.finance.cash;
  out['salary'] = state.finance.salary;
  out['burnoutLoad'] = state.burnout.load;
  return out;
}

const DELTA_LABELS: Record<string, string> = {
  health: 'Health',
  energy: 'Energy',
  stress: 'Stress',
  happiness: 'Happiness',
  purpose: 'Purpose',
  cash: 'Cash',
  salary: 'Salary',
  burnoutLoad: 'Burnout load',
};

function diff(before: Record<string, number>, after: Record<string, number>): StatDelta[] {
  const out: StatDelta[] = [];
  for (const key of Object.keys(before)) {
    const b = before[key] as number;
    const a = after[key] as number;
    if (Math.round(b) !== Math.round(a)) {
      out.push({ key, label: DELTA_LABELS[key] ?? key, before: Math.round(b), after: Math.round(a) });
    }
  }
  return out;
}

export function resolveChoice(state: PlayerState, event: GameEvent, choiceId: string): ResolutionResult {
  const choice = event.choices.find((c) => c.id === choiceId);
  if (!choice) {
    return { narrative: 'Nothing happened.', outcomeLabel: 'No change', deflected: false, entries: [], deltas: [] };
  }

  const rng = Rng.fromState(state.rng);
  const ctx: EffectContext = { state, rng, source: event.title, sourceEventId: event.id };
  const logStart = state.log.length;
  const before = snapshot(state);

  // Record that the event fired.
  state.eventMemory.lastSeenAge[event.id] = state.age;
  state.eventMemory.counts[event.id] = (state.eventMemory.counts[event.id] ?? 0) + 1;

  let deflected = false;
  let narrative: string;
  let outcomeLabel: string;

  // Phase 1 — deliberate deferral. The player chose to not act.
  if (choice.defers) {
    applyEffects(ctx, [
      {
        k: 'intention',
        op: 'add',
        id: choice.defers.id,
        label: choice.defers.label,
        topic: choice.defers.topic,
        ...(choice.defers.urgency !== undefined ? { urgency: choice.defers.urgency } : {}),
        ...(choice.defers.resumeEventId ? { resumeEventId: choice.defers.resumeEventId } : {}),
        ...(choice.defers.decayEventId ? { decayEventId: choice.defers.decayEventId } : {}),
      },
    ]);
  }

  // Phase 2 — inertia. The player intended to act and did not.
  const risk = inertiaChance(state, choice.inertiaRisk ?? 0);
  if (risk > 0 && rng.chance(risk)) {
    deflected = true;
    outcomeLabel = 'It never happened';
    narrative =
      'You meant to. You genuinely intended to. Somehow the weeks went past and it did not get done.';
    applyEffects(ctx, [
      { k: 'inertia', op: 'pressure', delta: 9 },
      { k: 'habit', habit: 'procrastination', delta: 5 },
      { k: 'stat', stat: 'happiness', delta: -3 },
    ]);
    pushLog(state, {
      category: 'inertia',
      tone: 'negative',
      title: `Intended to: ${choice.label}`,
      detail: 'And did not.',
      weight: 3,
      tags: ['inertia', 'deflected'],
    });
  } else {
    applyEffects(ctx, choice.cost);
    const outcome = pickOutcome(state, choice, rng);
    if (!outcome) {
      narrative = 'Nothing came of it.';
      outcomeLabel = 'No change';
    } else {
      applyEffects(ctx, outcome.effects);
      narrative = outcome.narrative;
      outcomeLabel = outcome.label;
      const tone = outcome.tone ?? 'neutral';
      pushLog(state, {
        category: event.category === 'romance' ? 'relationship' : mapCategory(event.category),
        tone,
        title: event.title,
        detail: outcome.label,
        weight: tone === 'neutral' ? 2 : 4,
        tags: [...(event.tags ?? []), ...(choice.tags ?? [])],
      });
    }
    if (!choice.defers) {
      state.inertia.actedCount += 1;
    }
  }

  const record: DecisionRecord = {
    age: state.age,
    eventId: event.id,
    eventTitle: event.title,
    choiceId: choice.id,
    choiceLabel: choice.label,
    outcomeLabel,
    tags: choice.tags ?? [],
  };
  state.decisions.push(record);

  state.rng = rng.toState();
  const entries = state.log.slice(logStart);
  return { narrative, outcomeLabel, deflected, entries, deltas: diff(before, snapshot(state)) };
}

function mapCategory(category: GameEvent['category']): LogEntry['category'] {
  switch (category) {
    case 'romance':
      return 'relationship';
    case 'opportunity':
      return 'career';
    case 'social':
      return 'relationship';
    case 'leisure':
      return 'wellbeing';
    default:
      return category;
  }
}
